import { type ReactNode, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import styled from 'styled-components'

import { spacing } from 'ui-primitives/src/utils/spacing'
import { InfoIcon } from 'bl-common/src/elements/Icons/InfoIcon'
import {
  Type,
  type TypeProps,
  type Weight,
} from 'bl-common/src/elements/Typography/Typography'
import { formatHtmlText } from 'bl-utils/src/formatting/formatHtmlText'

export type DisclaimerProps = {
  children: ReactNode
  showDisclaimer?: boolean
  scrollIntoView?: boolean
  type?: 'border' | 'circle'
  severity?: 'error' | 'warning' | 'info' | 'success'
  color?: string
  fontWeight?: Weight
} & TypeProps

export const Disclaimer = ({
  children,
  showDisclaimer = true,
  scrollIntoView,
  ...displaimerProps
}: DisclaimerProps) => {
  /*
  Note: If you use the condition on the field to control visibility of the disclaimer,
  it will animate in but not out, if you want it to animate both in and out
  you have to use showDisclaimer instead
  */

  const elementRef = useRef<HTMLDivElement>()

  useEffect(() => {
    if (showDisclaimer && scrollIntoView) {
      elementRef?.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [showDisclaimer, scrollIntoView])

  return (
    <AnimatePresence>
      {showDisclaimer && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ ease: 'linear', duration: 0.2 }}
        >
          <div ref={elementRef} style={{ scrollMarginTop: 50 }}>
            <BaseDisclaimer {...displaimerProps}>{children}</BaseDisclaimer>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const StyledDisclaimer = styled.div<{
  severity?: DisclaimerProps['severity']
  $color?: DisclaimerProps['color']
}>`
  position: relative;
  padding-left: ${spacing[1]};
  ::after {
    content: '';
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    width: 2px;
    background: ${({ severity, $color }) =>
      $color || `var(--color-${severity}, var(--color-accent))`};
  }

  a {
    color: var(--color-link);
  }
`

const StyledDisclaimerText = styled.div`
  align-items: flex-start;
  display: flex;
  order: -1;
`
const Icon = styled(InfoIcon)`
  flex: 0 0 ${spacing[1.5]};
  padding-top: 2px;

  ${`
    flex: 0 0 ${spacing[1]};
  `}
`

export const BaseDisclaimer = ({
  children,
  type,
  severity,
  color,
  preset = 'labelSmall',
  fontWeight,
  ...rest
}: DisclaimerProps) => {
  if (!children) {
    return null
  }

  if (type === 'circle') {
    return (
      <StyledDisclaimerText>
        <Icon color={color || `var(--color-${severity})`} />
        <Type preset="text" weight={fontWeight} left={{ xs: 0.5 }} {...rest}>
          {children}
        </Type>
      </StyledDisclaimerText>
    )
  }
  return (
    <StyledDisclaimer $color={color} severity={severity}>
      {typeof children === 'string' ? (
        <Type
          preset={preset}
          weight={fontWeight}
          multiline
          dangerouslySetInnerHTML={{
            __html: formatHtmlText(children),
          }}
          {...rest}
        />
      ) : (
        children
      )}
    </StyledDisclaimer>
  )
}
